import type { MessageChannelV3 } from "@redotech/redo-flows-common/rules-v3/types";
import { AssignV3Strategy } from "@redotech/redo-flows-common/rules/actions";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { DefaultRule } from "../types";

/**
 * The transports Shopper Agent email replies turn on. The reply rule's channel
 * list and this rule's gate are both built from it, so the two cannot drift.
 */
export const SHOPPER_AGENT_EMAIL_REPLY_CHANNELS = [
  "gmail",
  "outlook",
  "customEmail",
] as const satisfies readonly MessageChannelV3[];

/**
 * Default rule: Unassign AI on a Shopper Agent email conversation
 *
 * The mirror image of `default-send-ai-response-on-shopper-agent-conversation`
 * for a team that has not turned Shopper Agent email replies on. That rule's
 * channel gate leaves an AI-owned email lane untouched, so a customer who
 * emails back sits with an assignee that will never answer. This rule releases
 * the ticket instead, so it re-enters the routing queue.
 *
 * Gates on the lane, AI ownership, and the email transports, nothing else: a
 * silent AI assignee is wrong whatever the ticket is tagged, so the reply
 * rule's suppression tags are deliberately not mirrored. `enabled` is the only
 * thing that varies:
 * `syncShopperAgentEmailReplyRuleConfig` writes it as the inverse of the reply
 * rule's email opt-in, so exactly one of the two can act on an email lane.
 * Email replies are opt-in, so the registry default is on.
 *
 * Why a sibling rule rather than a false edge on the reply rule:
 * `evaluateRulePrefilter` reads only a rule's first conditions step and drops
 * the whole rule when that step is definitely false, edges included, so a
 * false branch behind the channel gate would never run. One flat step keeps
 * every gate prunable: non-email tickets and agent-off teams never start the
 * graph workflow that `ASSIGN_V3` needs.
 *
 * Shares the reply rule's `sortIndex`: the two are mutually exclusive on every
 * message, so their relative order is moot, and hidden rules do not count
 * toward `maxVisibleDefaultRuleSortIndex`.
 */
export const unassignAiOnShopperAgentEmailConversationRule = {
  id: "default-unassign-ai-on-shopper-agent-email-conversation",
  name: "Unassign AI on a Shopper Agent email conversation",
  description:
    "When a customer emails on a Shopper Agent conversation the AI owns while Shopper Agent email replies are off, unassign the AI so the conversation re-enters the routing queue.",
  hidden: true,
  triggerType: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,

  defaultConfig: {
    defaultRuleId: "default-unassign-ai-on-shopper-agent-email-conversation",
    enabled: true,
    sortIndex: 1,
    configurations: {},
  },

  template: {
    name: "Unassign AI on a Shopper Agent email conversation",
    description:
      "When a customer emails on a Shopper Agent conversation the AI owns while Shopper Agent email replies are off, unassign the AI so the conversation re-enters the routing queue.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
        nextId: "shopper-agent-email-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "shopper-agent-email-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: [...SHOPPER_AGENT_EMAIL_REPLY_CHANNELS],
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.SHOPPER_AGENT_CONVERSATION,
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
          ],
        },
        nextTrueId: "unassign-ai-actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "unassign-ai-actions",
        actions: [
          {
            actionType: RuleActionType.ASSIGN_V3,
            mode: { _tag: "unassign" },
            strategy: AssignV3Strategy.ROUND_ROBIN,
            maxTicketsPerUser: null,
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule;
